<script setup>
import {useConfigurateurStore} from "../../stores/configurateur";
import {computed, onMounted, ref, nextTick} from "vue";
import {formatDate} from "../../../../../../packages/Divioseo/GTDrive/src/Resources/assets/js/date";
import cadeauImageDefault from "@/img/card-push-img.jpg";
import dateImageDefault from "@/img/gallery-exemple2.jpg";

const store = useConfigurateurStore();

// Images des cartes « Bon valable un an » / « Choisir une date » : configurables en BO
// (/admin/configuration → GTDrive → Configurateur), avec fallback sur l'image par défaut.
const cadeauImage = computed(() => store.configurateurImages?.cadeau || cadeauImageDefault);
const dateImage = computed(() => store.configurateurImages?.date || dateImageDefault);
const currentCreneau = ref(null);
const goingForward = ref(true);
const isCollapsed = ref(false);
const showModal = ref(false);
const selectedDateForModal = ref(null);

const handleChoiceMonth = (month) => {
    store.setStepDate('day')
    store.setCurentMonth(month)
    goingForward.value = true
}

const handleChoiceDate = (date) => {
    store.setStepDate('creneau')
    store.setCurentDate(date)
    goingForward.value = true
}

const handleChoiceCreneau = (creneau) => {
    currentCreneau.value = creneau;
    store.circuit_date_creneau_id = creneau.circuit_date_creneau_id
    store.setCircuitDateId(creneau.circuit_date_id)
    store.circuit_date_creneau_voiture_id = creneau.circuit_date_creneau_voiture_id
    store.creneau_human = store.currentDate.date_human + ' ' + creneau.creneau_debut + ' - ' + creneau.creneau_fin
    store.date = formatDate(store.currentDate.date)
    store.creneau = creneau.creneau_debut + '-' + creneau.creneau_fin
    store.setStepDate('month')

    store.cleanStageIds()

    // Fermer la modal si elle est ouverte
    if (showModal.value) {
        closeModal();
    }
}

const handleBack = () => {
    switch (store.stepDate) {
        case 'day':
            store.setStepDate('month')
            break;
        case 'creneau':
            store.setStepDate('day')
            break;
    }
    goingForward.value = false
}

const transitionName = computed(() => {
    return goingForward ? 'slide-right' : 'slide-left';
});

const currentStage = computed(() => {
    // Select appropriate stage based on experience mode
    if (store.experience === 'bapteme') {
        return store.stageBapteme;
    }
    return store.stage;
});

const handleChoiceDateMode = () => {
    if (store.dateMode == 'cadeau') {
        store.setStepDate('month')
        store.setCurentMonth(null)
        store.setCurentDate(null)
        currentCreneau.value = null;
        store.circuit_date_creneau_id = null
        store.circuit_date_id = null
        store.circuit_date_creneau_voiture_id = null
        store.creneau_human = null
        store.date = null
        store.creneau = null
    }
}

const toggleCollapse = () => {
    isCollapsed.value = !isCollapsed.value;
}

const showDateInfo = (modeInfo, event) => {
    selectedDateForModal.value = modeInfo;
    showModal.value = true;
}

const showDateChoiceModal = () => {
    selectedDateForModal.value = {mode: 'date-choice', label: 'Choisir une date'};
    showModal.value = true;
}

const closeModal = () => {
    showModal.value = false;
    selectedDateForModal.value = null;
}


</script>

<template>
    <section id="step-creneau">
        <div class="card-step-group shadow-sm is-sticky">
            <div class="step-title d-flex justify-content-between align-items-center">
                <div class="circle"
                     :class="{'active': store.getStepNumber('creneau') == store.step, 'completed': store.isCompleted(store.getStepNumber('creneau'))}">
                    {{ store.getStepNumber('creneau') }}
                </div>
                <span class="h5 large fw-bolder mb-2">{{ $t('Date') }} & {{ $t('Horaire') }}</span>
                <button class="btn btn-sm" type="button" @click="toggleCollapse">
                    <i class="bi" :class="isCollapsed ? 'bi-plus-circle' : 'bi-dash-circle'"></i>
                </button>
            </div>
            <div id="collapse-creneau"
                 class="collapse-content"
                 :class="{ 'collapsed': isCollapsed }">

                <div class="card-title m-3" v-if="store.dateMode == 'choice'">
                    <div class="row align-items-center">
                        <div class="col-12 mb-3" v-if="store.currentDate">
                            <span class="text-gray-60">Date sélectionnée</span>
                            <span class="ms-1">{{ store.currentDate.date_human }}</span>
                        </div>

                        <div class="col-12" v-if="currentCreneau">
                            <span class="text-gray-60">Heure de présentation sur place</span>
                            <span class="ms-1">{{
                                    currentCreneau.creneau_debut
                                }} - {{ currentCreneau.creneau_fin }}</span>
                        </div>

                        <!-- Bouton pour choisir l'heure quand date pré-sélectionnée -->
                        <div class="col-12 mt-3"
                             v-if="store.currentDate && store.stepDate == 'creneau' && !currentCreneau">
                            <button @click.prevent="showDateChoiceModal" type="button" class="btn btn-primary btn-sm">
                                <i class="bi bi-clock me-2"></i>
                                Je choisis mon heure de passage
                            </button>
                        </div>
                    </div>
                </div>

                <div class="card card-step card-body">
                    <div class="row">
                        <div class="col-6 col-xl-6 col-xxxl-4 p-2">
                            <div class="card card-config">
                                <div class="form-check form-switch">
                                    <input @change="handleChoiceDateMode" v-model="store.dateMode" value="cadeau"
                                           class="form-check-input" type="radio" name="dateMode" id="cadeau">
                                </div>
                                <div class="card-clickable-area">
                                    <i class="bi bi-info-circle-fill show-infos"
                                       @click.stop.prevent="showDateInfo({mode: 'cadeau', label: 'Bon valable un an'})"
                                       title="Voir les détails de cette option"></i>
                                    <img :src="cadeauImage" alt=""
                                         class="card-config-bg card-config-bg-cover">

                                    <div class="card-body">
                                        <div class="card-text">
                                            <span class="m-0 fw-bolder">{{ $t('Bon valable un an') }}</span>
                                        </div>
                                        <!-- Badge pour la promo en cours -->
                                        <div v-if="currentStage.prices?.normal?.promo"
                                             class="badge bg-danger text-white text-center fw-bolder">
                                            -{{ currentStage.prices.normal.promo }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="col-6 col-xl-6 col-xxxl-4 p-2">
                            <div class="card card-config">
                                <div class="card-clickable-area" @click="showDateChoiceModal">
                                    <div class="form-check form-switch">
                                        <input v-model="store.dateMode" value="choice" class="form-check-input"
                                               type="radio"
                                               name="dateMode" id="choice-date">
                                    </div>
                                    <i class="bi bi-info-circle-fill show-infos"
                                       @click.stop.prevent="showDateInfo({mode: 'choice', label: 'Choisir une date'})"
                                       title="Voir les détails de cette option"></i>
                                    <img :src="dateImage" alt=""
                                         class="card-config-bg card-config-bg-cover">
                                    <div class="card-body">
                                        <div class="card-text">
                                            <span class="m-0 fw-bolder">{{ $t('Choisir une date') }}</span>
                                        </div>
                                        <!-- Badge pour la meilleure promo -->
                                        <div v-if="currentStage?.promo"
                                             class="badge bg-danger text-white text-center fw-bolder">
                                            -{{ currentStage.promo }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="col-6 col-xl-6 col-xxxl-4 p-2">
                            Choisir une date vous permet de profiter de tarifs plus avantageux selon les périodes
                        </div>
                    </div>
                </div>
            </div>

            <!-- Modal pour les informations du mode de réservation -->
            <div v-if="showModal" class="step-modal-overlay" @click="closeModal">
                <div class="step-modal-content" @click.stop>
                    <div class="step-modal-header">
                        <h3 class="step-modal-title">
                            {{ selectedDateForModal?.label }}
                        </h3>
                        <button type="button" class="btn-close" @click="closeModal">
                            <i class="bi bi-x-lg"></i>
                        </button>
                    </div>
                    <div class="step-modal-body">
                        <div v-if="selectedDateForModal" class="card-mode-infos">
                            <div class="card-text">
                                <template v-if="selectedDateForModal.mode === 'cadeau'">
                                    <p><strong>Mode Cadeau</strong></p>
                                    <p>En choisissant cette option, vous offrez une expérience de pilotage sans date
                                        fixe. Le bénéficiaire pourra choisir sa date de réservation ultérieurement selon
                                        ses disponibilités.</p>
                                    <ul>
                                        <li>Validité du bon cadeau : 12 mois</li>
                                        <li>Réservation flexible selon disponibilités</li>
                                        <li>Possibilité de reporter la date</li>
                                    </ul>
                                </template>

                                <template v-if="selectedDateForModal.mode === 'pilote'">
                                    <p><strong>Je suis le pilote</strong></p>
                                    <p>En choisissant cette option, vous vous offrez une expérience de pilotage sans
                                        date fixe. Vous pourrez choisir la date de réservation ultérieurement selon vos
                                        disponibilités.</p>
                                    <ul>
                                        <li>Validité du bon cadeau : 12 mois</li>
                                        <li>Réservation flexible selon disponibilités</li>
                                        <li>Possibilité de reporter la date</li>
                                    </ul>
                                </template>

                                <template v-if="selectedDateForModal.mode === 'choice'">
                                    <p><strong>Choisir une date</strong></p>
                                    <p>Sélectionnez cette option pour parcourir le calendrier des disponibilités et
                                        choisir votre créneau de pilotage.</p>
                                    <ul>
                                        <li>Visualisation du calendrier complet</li>
                                        <li>Créneaux disponibles par jour</li>
                                        <li>Tarifs selon la période</li>
                                        <li>Réservation immédiate</li>
                                    </ul>
                                </template>

                                <template v-if="selectedDateForModal.mode === 'date-choice'">
                                    <div class="date-choice-content">
                                        <div class="mb-3">
                                            <div class="row align-items-center mb-3">
                                                <div class="col-12 mb-3" v-if="store.currentDate">
                                                    <span class="text-gray-60">Date sélectionnée</span>
                                                    <span class="ms-1">{{ store.currentDate.date_human }}</span>
                                                </div>

                                                <div class="col-12 mb-3" v-if="currentCreneau">
                                                    <span class="text-gray-60">Heure de présentation sur place</span>
                                                    <span class="ms-1">{{
                                                            currentCreneau.creneau_debut
                                                        }} - {{ currentCreneau.creneau_fin }}</span>
                                                </div>
                                            </div>

                                            <template v-if="store.stepDate != 'month'">
                                                <button @click.prevent="handleBack"
                                                        class="btn btn-secondary btn-sm mb-3">{{
                                                        $t('Retour')
                                                    }}
                                                </button>
                                            </template>

                                            <div class="date-selection-container">
                                                <Transition :name="transitionName" mode="out-in">
                                                    <div class="row row-cols-1 row-cols-sm-2  g-2"
                                                         v-if="store.stepDate == 'month'">
                                                        <div v-for="(month, index) in currentStage.months" :key="index"
                                                             class="d-grid">
                                                            <button @click.prevent="handleChoiceMonth(month)"
                                                                    :disabled="!month.dates.length"
                                                                    class="btn btn-block btn-sm btn-light position-relative">
                                                            <span
                                                                v-if="month.price && month.price.promo"
                                                                class="badge bg-secondary position-absolute top-0 start-0 translate-middle-y me-1 small">{{
                                                                    month.price.promo
                                                                }}</span>
                                                                <span
                                                                    class="badge bg-primary position-absolute top-0 end-0 translate-middle-y me-1 small">{{
                                                                        month.dates.length
                                                                    }}</span>
                                                                <small>{{ month.label }}</small>
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div v-if="store.stepDate == 'day'" class="mt-3">
                                                        <div v-for="(date, index) in store.currentMonth.dates"
                                                             :key="index"
                                                             class="d-grid mb-2">
                                                            <button @click.prevent="handleChoiceDate(date)"
                                                                    class="btn btn-block btn-sm btn-light position-relative">
                                                            <span
                                                                v-if="date.price && date.price.promo"
                                                                class="badge bg-secondary position-absolute top-0 start-0 translate-middle-y me-1 small">{{
                                                                    date.price.promo
                                                                }}</span>
                                                                <span
                                                                    class="badge bg-primary position-absolute top-0 end-0 translate-middle-y me-1 small">{{
                                                                        date.creneaux.length
                                                                    }}</span>
                                                                <small>{{ date.date_human }}</small>
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div v-if="store.stepDate == 'creneau'" class="mt-3">
                                                        <div v-for="(creneau, index) in store.currentDate.creneaux"
                                                             :key="index"
                                                             class="d-grid mb-2">
                                                            <button @click.prevent="handleChoiceCreneau(creneau)"
                                                                    class="btn btn-block btn-sm btn-light position-relative">
                                                            <span
                                                                v-if="creneau.price && creneau.price.promo"
                                                                class="badge bg-secondary position-absolute top-0 start-0 translate-middle-y me-1 small">{{
                                                                    creneau.price.promo
                                                                }}</span>
                                                                <small>{{ creneau.creneau_debut }} -
                                                                    {{ creneau.creneau_fin }}</small>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </Transition>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>

</template>

<style scoped>
.collapse-content {
    overflow: hidden;
    transition: max-height 0.3s ease-out;
    max-height: 2000px; /* Valeur suffisamment grande pour le contenu */
}

.collapse-content.collapsed {
    max-height: 0;
}

.slide-right-enter-active,
.slide-right-leave-active {
    transition: transform 0.1s ease-in-out;
}

.slide-right-enter-from {
    transform: translateX(100%);
    opacity: 0;
}

.slide-right-leave-to {
    transform: translateX(-100%);
    opacity: 0;
}

/* Styles pour slide-left (inversés) */
.slide-left-enter-active,
.slide-left-leave-active {
    transition: transform 0.1s ease-in-out;
}

.slide-left-enter-from {
    transform: translateX(-100%); /* <-  Changement ici */
    opacity: 0;
}

.slide-left-leave-to {
    transform: translateX(100%); /* <-  Changement ici */
    opacity: 0;
}

</style>
